"use client";

import Link from "next/link";
import Image from "next/image";
import {
  Facebook,
  Instagram,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Twitter,
} from "lucide-react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { figmaFooterColumns } from "@/data/figmaHome";

const Footer = () => {
  const { siteName, copyrightText, contact } = useSiteConfig();
  const supportPhone = contact?.phone || "9876543210";
  const supportEmail = contact?.email || "support@autospares.com";
  const supportAddress = contact?.address || "Raipur, Chhattisgarh, India";
  const supportPhoneHref = `tel:${String(supportPhone).replace(/\D/g, "")}`;

  return (
    <footer className="relative border-t border-white/10 bg-[#08080b] text-white px-6 sm:px-6 lg:px-8 xl:px-15">
      <div className="racing-stripe absolute inset-x-0 top-0 h-0.75 opacity-70" />
      <div className="grid w-full gap-10 py-12 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.2fr_1fr_1fr_1fr_1fr] lg:px-8 xl:px-10">
        <div className="max-w-sm">
          <div className="flex items-center gap-3">
            <Image
              src="/logo-emblem.png"
              alt={siteName || "Car Spares"}
              width={44}
              height={44}
              className="h-11 w-11 rounded-full"
            />
            <span className="text-lg font-extrabold tracking-[0.12em] text-white">
              CAR SPARES
            </span>
          </div>
          <p className="mt-5 text-sm leading-6 text-white/65">
            Your trusted marketplace for genuine car spare parts in India. We
            focus on fitment, quality, and dependable delivery.
          </p>

          <div className="mt-6 flex items-center gap-3">
            <Link
              href="#"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-400"
              aria-label="Facebook"
            >
              <Facebook className="h-4 w-4" />
            </Link>
            <Link
              href="#"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-400"
              aria-label="Instagram"
            >
              <Instagram className="h-4 w-4" />
            </Link>
            <Link
              href="#"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-400"
              aria-label="Twitter"
            >
              <Twitter className="h-4 w-4" />
            </Link>
            <Link
              href="#"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition hover:border-amber-500/40 hover:bg-amber-500/10 hover:text-amber-400"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {figmaFooterColumns.map((column) => (
          <div key={column.title}>
            <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
              {column.title}
            </h3>
            <ul className="mt-5 space-y-3">
              {column.links.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-sm text-white/65 transition hover:text-white"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <h3 className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
            Contact Support
          </h3>
          <div className="mt-5 space-y-4 text-sm text-white/65">
            <p className="flex items-start gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <span>{supportAddress}</span>
            </p>
            <p className="flex items-center gap-3">
              <Phone className="h-4 w-4 shrink-0 text-amber-400" />
              <a
                href={supportPhoneHref}
                className="transition hover:text-white"
              >
                {supportPhone}
              </a>
            </p>
            <p className="flex items-center gap-3">
              <Mail className="h-4 w-4 shrink-0 text-amber-400" />
              <a
                href={`mailto:${supportEmail}`}
                className="transition hover:text-white"
              >
                {supportEmail}
              </a>
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 mt-4 sm:mt-0">
        <div className="flex w-full gap-4 px-4 py-6 text-xs text-white/70 md:flex-row md:items-center md:justify-between sm:px-6 lg:px-8 xl:px-10 text-left">
          <p className="text-center md:text-left">
            {copyrightText ||
              `Copyright ${new Date().getFullYear()} ${
                siteName || "Car Spares"
              }. All rights reserved.`}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
